import { createInterface } from 'node:readline';
import type { AiUsage } from '@fim/ai-sdk';
import { normalizeTier, resolveTier, TIER_IDS, TIERS } from '@fim/futurex-protocol';
import type { EffortLevel, Tier } from '@fim/futurex-protocol';
import { type ReplAgent } from './agent.js';
import { createReplRuntime } from './repl-runtime.js';
import { Chatbox, createChatboxAsk, type Suggestion } from './chatbox.js';
import { createPermissionGate, type AskFn } from './permissions.js';
import { runPermissions } from './permissions-command.js';
import { currentPrefixDigest, loadContext } from './context.js';
import { bootstrapMemory } from './memory.js';
import { runCompact, runInit } from './memory-command.js';
import { createAiClient } from '@fim/ai-sdk';
import { executePlan } from './build-command.js';
import { evaluateGoal } from './goal.js';
import { runDiagnostics } from './diagnostics.js';
import { ensureBuildPlan, printBuildPlan, runPlan, runResume } from './plan-command.js';
import { pipelineId, readCheckpoint, writeCheckpoint } from './pipeline.js';
import { runSkills } from './skills-command.js';
import { runVerify } from './verify-command.js';
import { runImports, runSymbols } from './index-command.js';
import { buildRepoCard, findRepoRoot, MEMORY_FILENAME, readMemory } from './repo.js';
import { loadRetriever } from './index-store.js';
import { usageLine } from './render.js';
import { addUsage, emptyTotals, formatUsage } from './pricing.js';
import type { CliOptions } from './options.js';
import { color, error, info, line, setOutputSink, warn } from './ui.js';

/** Slash commands offered in the chatbox dropdown + Tab-completion. */
const SLASH_COMMANDS: Suggestion[] = [
  { value: '/help', hint: 'commands & usage' },
  { value: '/exit', hint: 'quit' },
  { value: '/clear', hint: 'reset the conversation' },
  { value: '/model', hint: 'switch fx-* module' },
  { value: '/tier', hint: 'switch fx-* module' },
  { value: '/effort', hint: 'reasoning effort' },
  { value: '/usage', hint: 'tokens + cost' },
  { value: '/permissions', hint: 'view/reset consent' },
  { value: '/auto', hint: 'toggle ask ↔ auto approve' },
  { value: '/context', hint: 'session context' },
  { value: '/memory', hint: 'project memory' },
  { value: '/skills', hint: 'available skills' },
  { value: '/symbols', hint: 'symbol index' },
  { value: '/imports', hint: 'import graph' },
  { value: '/init', hint: 'write the memory file' },
  { value: '/compact', hint: 'compact memory' },
  { value: '/plan', hint: 'plan a task' },
  { value: '/build', hint: 'execute a plan' },
  { value: '/resume', hint: 'resume a plan' },
  { value: '/verify', hint: 'typecheck + lint' },
  { value: '/test', hint: 'run tests' },
];

/** Reasoning-effort levels offered by the `/effort` ↑↓ dropdown. */
const EFFORT_LEVELS: EffortLevel[] = ['off', 'high', 'max'];
const EFFORT_HINT: Record<EffortLevel, string> = {
  off: 'no thinking · fastest',
  high: 'thinking · reasoning high',
  max: 'deep thinking · reasoning max',
};

/**
 * User-facing one-liners for the /model menu. FutureX modules are branded by
 * capability only — the underlying provider model id is NEVER shown to users.
 */
const TIER_HINT: Record<Tier, string> = {
  'fx-pro': 'deepest reasoning · hardest tasks',
  'fx-fast': 'quick · non-thinking (default)',
  'fx-mini': 'balanced · light reasoning',
  'fx-eco': 'cheapest · capped output',
};

/** One pending input action. */
type Action = { kind: 'cmd'; cmd: string; rest: string } | { kind: 'prompt'; text: string };

/**
 * Interactive REPL — an agentic, repo-aware coding session as a chatbox: the
 * input bar is pinned to the bottom (raw-mode {@link Chatbox}) while streaming
 * output, tool activity, and the spinner flow above it. Keep typing while a
 * prompt runs — extra prompts QUEUE and run in order. Falls back to a plain
 * readline loop when stdin/stdout isn't a TTY (piped input).
 */
export async function runRepl(opts: CliOptions): Promise<void> {
  // Bootstrap project memory on a new session, BEFORE loadContext so the pinned
  // prefix includes it from the start.
  if (opts.useMemory) {
    const root = findRepoRoot();
    if (readMemory(root) === null) {
      const card = opts.useRepo ? buildRepoCard(root) : null;
      const symbols = loadRetriever(root)?.symbols ?? null;
      if (bootstrapMemory(root, card, symbols)) {
        info(`wrote ${MEMORY_FILENAME} (project memory) — edit it to guide FutureX`);
      }
    }
  }
  const ctx = loadContext(opts);

  let tier: Tier = opts.tier ?? 'fx-fast'; // /model and /tier change this mid-session
  // /effort override (null = tier default); applies to /plan + /build. Seeded from
  // the CLI --effort flag when it's valid for the starting tier, else null.
  let effort: EffortLevel | null =
    opts.effort && TIERS[tier].allowedEfforts.includes(opts.effort) ? opts.effort : null;
  const totals = emptyTotals();
  const queue: Action[] = [];
  let running = false;
  let closed = false;
  let active: AbortController | null = null;
  let prefixDriftWarned = false;

  let box: Chatbox | null = null;
  let rl: ReturnType<typeof createInterface> | null = null;
  let finish: () => void = () => {};
  let agentRuntime: ReplAgent;
  const agentOptions = { autoApprove: false, maxSteps: 32, verify: 'auto' as const };
  // Mutable so Shift+Q / `/auto` can flip it live: createPermissionGate reads
  // `autoApprove` on THIS object per request, so a toggle takes effect at once.
  const permMode = { autoApprove: agentOptions.autoApprove };

  const waitingPrompts = (): number => queue.filter((a) => a.kind === 'prompt').length;

  /** Dynamic status text shown on the chatbox's bottom line (dot added by the box). */
  const statusLine = (): string => {
    const mod = effort ? `${tier}·${effort}` : tier;
    const bits = [running ? 'working' : 'ready', mod, `${agentRuntime.toolCount} tools`, 'verify auto'];
    // Permission mode is independent of verify — show it as its own flag.
    bits.push(permMode.autoApprove ? '⚠ auto-approve' : 'ask perms');
    const waiting = waitingPrompts();
    if (waiting > 0) bits.push(`${waiting} queued`);
    return `${bits.join(' · ')}    /help · shift+tab auto · ctrl+c cancels`;
  };

  /** Repaint the chatbox status, or re-show the readline prompt when idle. */
  const refresh = (): void => {
    if (box) box.refresh();
    else if (!running && !closed) rl?.prompt();
  };

  /** Ctrl+C: cancel the in-flight turn + drop anything queued; otherwise quit. */
  const interrupt = (): void => {
    if (active && !active.signal.aborted) {
      active.abort();
      queue.length = 0;
      box?.refresh();
    } else {
      finish();
    }
  };

  /**
   * Flip the session permission mode (ask ↔ auto). AUTO approves every mutating
   * tool call (edit/create/delete/run) WITHOUT prompting — the gate short-circuits
   * before any store lookup or modal. ASK restores the per-kind consent flow.
   * Bound to `/auto` and to Shift+Tab.
   */
  const toggleAuto = (): void => {
    permMode.autoApprove = !permMode.autoApprove;
    info(
      permMode.autoApprove
        ? 'permissions → AUTO — approving all edits, writes & commands without asking'
        : 'permissions → ASK — will prompt before edits, writes & commands',
    );
    box?.refresh();
  };

  /** `/plan --restart <task>` style prefix flags for the REPL plan/build commands. */
  const replPlanFlags = (rest: string): { task: string; restart: boolean } => {
    let task = rest.trim();
    let restart = false;
    if (task.startsWith('--restart')) {
      restart = true;
      task = task.slice('--restart'.length).trim();
    }
    return { task, restart };
  };

  /** CliOptions re-routed through the LIVE tier + effort (changed via /model, /effort). */
  const liveOpts = (): CliOptions => {
    // `effort` is validated against the tier's allowed set on /effort and reset on
    // /model, so resolveTier never throws here.
    const r = resolveTier(tier, effort ? { effort } : {});
    return { ...opts, model: r.model, effort: r.effort, reasoning: r.reasoning, tier };
  };

  /**
   * `/build [--restart] <task>` — produce (or resume) the pre-build plan, then
   * execute its task DAG through THIS session's agent runtime + permission
   * gate, stopping when the acceptance criteria are met or the token budget
   * runs out.
   */
  const runBuildInRepl = async (rest: string): Promise<void> => {
    const { task: q, restart } = replPlanFlags(rest);
    if (!q) {
      error('usage: /build [--restart] <task>');
      return;
    }
    // The controller must exist BEFORE the (multi-model-call) planning phase so
    // Ctrl+C cancels the build instead of tearing the whole REPL down.
    const ac = new AbortController();
    active = ac;
    try {
      const buildOpts = liveOpts();
      const outcome = await ensureBuildPlan(q, buildOpts, { restart }, (name, status) =>
        info(`  ${status === 'cached' ? '✓ resumed' : '… running'} ${name}`),
      );
      if (ac.signal.aborted || closed) return;
      if (outcome.kind === 'clarify') {
        info('one question before building:');
        line(`  ? ${outcome.question}`);
        return;
      }
      printBuildPlan(outcome.plan);
      const model = buildOpts.model;
      const ai = createAiClient({ provider: 'deepseek' });
      const buildTotals = emptyTotals();
      const planId = pipelineId(`${ctx.root}::${q}`);
      const result = await executePlan({
        plan: outcome.plan,
        signal: ac.signal,
        async turn(prompt) {
          const usage = await agentRuntime.turn(prompt, model, ac.signal);
          if (usage) {
            addUsage(buildTotals, model, usage);
            addUsage(totals, model, usage);
          }
        },
        evaluate: (evidence) => evaluateGoal(outcome.plan.goal, evidence, ai, { model }),
        verify: () => runDiagnostics(ctx.root).map((r) => ({ name: r.name, ok: r.ok })),
        usedTokens: () => buildTotals.outputTokens + buildTotals.reasoningTokens,
        isTaskDone: (taskId) => readCheckpoint(planId, `exec-${taskId}`) !== null,
        markTaskDone: (taskId) => writeCheckpoint(planId, `exec-${taskId}`, { completed: true }),
        report: (text) => info(text),
      });
      info(
        `build ${result.status} · ${result.completedTasks.length}/${outcome.plan.tasks.length} tasks · ` +
          formatUsage(buildTotals).join(' · '),
      );
      if (result.evaluation && !result.evaluation.done) {
        info(`remaining criteria: ${result.evaluation.remaining.join(', ')}`);
      }
    } catch (err) {
      if (!ac.signal.aborted) error(err instanceof Error ? err.message : String(err));
      else info('build cancelled');
    } finally {
      active = null;
    }
  };

  /** Handle a `/command rest`. Returns 'exit' to leave the REPL. */
  const runCommand = async (cmd: string, rest: string): Promise<'exit' | void> => {
    switch (cmd) {
      case 'exit':
      case 'quit':
        return 'exit';
      case 'clear':
        agentRuntime.clear();
        info('conversation cleared');
        return;
      case 'help':
        line(color.bold('How it works'));
        line(color.dim('  Type what you want built. Every coding prompt runs plan → build → verify:'));
        line(color.dim('  the agent plans, edits files (writes ask first), then runs your typecheck/'));
        line(color.dim('  lint/tests and fixes failures before it declares the task done.'));
        line(color.dim('  Keep typing while one runs — extra prompts queue and run in order.'));
        line(color.bold('Commands'));
        line(color.dim(`  /model | /tier [${TIER_IDS.join('|')}]   /effort [off|high|max]   /usage`));
        line(color.dim('  /permissions [show|set <kind> <ask|allow|deny>|reset]'));
        line(color.dim('  /auto  (or Shift+Tab)  toggle ask ↔ auto-approve'));
        line(color.dim('  /context [show|clear]   /memory [show|compact]   /skills [request]'));
        line(color.dim('  /init   /compact   /plan <task>   /build <task>   /resume [id]'));
        line(color.dim('  /symbols [name]   /imports [file]   /verify   /test'));
        line(color.dim('  /clear   /help   /exit'));
        return;
      case 'model':
      case 'tier': {
        if (!rest) {
          info(`module: ${tier} (${TIERS[tier].mode})${effort ? ` · effort ${effort}` : ''} — type "/model " for the ↑↓ menu`);
          return;
        }
        const norm = normalizeTier(rest);
        if (!norm) {
          error(`unknown module "${rest}" (expected: ${TIER_IDS.join(' | ')})`);
          return;
        }
        tier = norm;
        effort = null; // a new tier has its own allowed efforts — drop the override
        info(`module → ${tier} (${TIERS[tier].mode})`);
        return;
      }
      case 'effort': {
        const allowed = TIERS[tier].allowedEfforts;
        if (!rest) {
          info(`effort: ${effort ?? `${resolveTier(tier).effort} (tier default)`} · allowed for ${tier}: ${allowed.join(' | ')}`);
          info('type "/effort " for the ↑↓ menu — applies to /plan and /build');
          info('(interactive prompts run fast/non-thinking for reliable tool calls)');
          return;
        }
        const lvl = rest.trim().toLowerCase();
        if (lvl !== 'off' && lvl !== 'high' && lvl !== 'max') {
          error(`unknown effort "${rest}" (expected: off | high | max)`);
          return;
        }
        if (!allowed.includes(lvl)) {
          error(`effort '${lvl}' is not allowed for ${tier} (allowed: ${allowed.join(', ')})`);
          return;
        }
        effort = lvl;
        info(`effort → ${effort} · applies to /plan and /build`);
        return;
      }
      case 'usage':
        if (totals.calls === 0) info('no usage yet this session');
        else info(`usage · ${formatUsage(totals).join(' · ')}`);
        return;
      case 'permissions':
        runPermissions(ctx.root, rest);
        return;
      case 'memory': {
        if (rest.toLowerCase() === 'compact') {
          runCompact();
          return;
        }
        const mem = readMemory(ctx.root);
        if (!mem) info(`no ${MEMORY_FILENAME} — run /init`);
        else line(mem.length > 1200 ? `${mem.slice(0, 1200)}\n…(truncated)` : mem);
        return;
      }
      case 'context': {
        if (rest.toLowerCase() === 'clear') {
          agentRuntime.clear();
          info('conversation context cleared');
          return;
        }
        const idx = ctx.retriever
          ? `${ctx.retriever.size} chunks · ${ctx.retriever.symbols.stats().symbols} symbols · ${ctx.retriever.imports.stats().edges} imports`
          : 'no index';
        info(`context · history ${agentRuntime.size()} msgs · index ${idx} · prefix ${ctx.systemDigest}`);
        return;
      }
      case 'skills':
        runSkills(rest);
        return;
      case 'symbols':
        runSymbols(rest);
        return;
      case 'imports':
        runImports(rest);
        return;
      case 'init':
        runInit();
        return;
      case 'compact':
        runCompact();
        return;
      case 'plan': {
        const { task: planTask, restart } = replPlanFlags(rest);
        // runPlan sets process.exitCode on failure for the one-shot CLI; a
        // failed /plan must not poison the eventual REPL exit code.
        const priorExitCode = process.exitCode;
        await runPlan(planTask, liveOpts(), { restart });
        process.exitCode = priorExitCode;
        return;
      }
      case 'build':
        await runBuildInRepl(rest);
        return;
      case 'resume':
        runResume(rest || undefined);
        return;
      case 'test':
        await runVerify({ only: 'test' });
        return;
      case 'verify':
      case 'run':
        await runVerify();
        return;
      default:
        info(`unknown command: /${cmd} (try /help)`);
        return;
    }
  };

  /** Run one prompt agentically (one queued item). */
  const runTurn = async (text: string): Promise<void> => {
    if (!prefixDriftWarned && currentPrefixDigest(ctx, opts) !== ctx.systemDigest) {
      prefixDriftWarned = true;
      warn('futurex-memory.md / repo changed on disk — keeping the session prefix (restart to apply)');
    }
    const ac = new AbortController();
    active = ac;
    const model = resolveTier(tier).model;
    let usage: AiUsage | undefined;
    try {
      usage = await agentRuntime.turn(text, model, ac.signal);
    } catch (err) {
      if (!ac.signal.aborted) error(err instanceof Error ? err.message : String(err));
    }
    active = null;
    if (usage) {
      addUsage(totals, model, usage);
      info(usageLine(usage, tier));
    }
  };

  /** Drain the queue one action at a time, then settle the prompt when idle. */
  const drain = async (): Promise<void> => {
    if (running) return;
    running = true;
    box?.refresh();
    while (queue.length > 0 && !closed) {
      const action = queue.shift();
      if (!action) break;
      if (action.kind === 'cmd') {
        // A slash command that throws must not reject drain() (that becomes an
        // unhandled rejection → the process dies WITHOUT the chatbox teardown,
        // leaking raw mode / bracketed paste into the shell). runTurn already
        // catches its own errors; mirror that for commands.
        try {
          if ((await runCommand(action.cmd, action.rest)) === 'exit') {
            finish();
            break;
          }
        } catch (err) {
          error(err instanceof Error ? err.message : String(err));
        }
      } else {
        await runTurn(action.text);
        box?.flushLive();
        if (!box) {
          const waiting = waitingPrompts();
          if (waiting > 0) info(color.dim(`↳ ${waiting} more queued…`));
        }
      }
      box?.refresh();
    }
    running = false;
    refresh();
  };

  /** Accept a submitted line: route /commands, else queue a prompt; /exit quits now. */
  const submit = (raw0: string): void => {
    const trimmed = raw0.trim();
    if (!trimmed) {
      refresh();
      return;
    }
    // Slash commands are SINGLE-LINE only. A multi-line paste is always a prompt,
    // even when its first line starts with '/' (a pasted path or //comment) —
    // otherwise the whole block is misrouted to an unknown command and dropped.
    if (!raw0.includes('\n') && trimmed.startsWith('/')) {
      const sp = trimmed.indexOf(' ');
      const cmd = (sp === -1 ? trimmed.slice(1) : trimmed.slice(1, sp)).toLowerCase();
      const rest = sp === -1 ? '' : trimmed.slice(sp + 1).trim();
      if (cmd === 'exit' || cmd === 'quit') {
        finish();
        return;
      }
      // Toggle permission mode NOW rather than queuing behind an in-flight turn,
      // so it matches the immediacy of the Shift+Tab keybind.
      if (cmd === 'auto') {
        toggleAuto();
        return;
      }
      queue.push({ kind: 'cmd', cmd, rest });
    } else {
      // Preserve the prompt VERBATIM (first-line indentation + internal newlines);
      // strip only trailing whitespace. Trimming the whole buffer would de-indent
      // the first line of a pasted code block relative to the lines below it.
      queue.push({ kind: 'prompt', text: raw0.replace(/\s+$/, '') });
    }
    box?.refresh();
    void drain();
  };

  /** Live suggestions as the user types (chatbox only): command names, then the
   * value menus for `/model` · `/tier` (tiers) and `/effort` (levels). */
  const onChange = (buf: string): void => {
    if (!box) return;
    const t = buf.trimStart();
    const lower = t.toLowerCase();
    // Command-name menu: "/mod" → /model, /memory, …
    if (lower.startsWith('/') && !lower.includes(' ')) {
      box.setSuggestions(SLASH_COMMANDS.filter((c) => c.value.startsWith(lower)).slice(0, 6));
      return;
    }
    // Argument menu for /model, /tier (tiers) and /effort (levels), arrow-selectable.
    const arg = t.match(/^\/(model|tier|effort)\s+(\S*)$/i);
    if (arg) {
      const cmd = (arg[1] ?? '').toLowerCase();
      const partial = (arg[2] ?? '').toLowerCase();
      if (cmd === 'effort') {
        // Only offer efforts the CURRENT tier actually allows — otherwise the menu
        // advertises a level the command would reject on submit.
        const allowed = TIERS[tier].allowedEfforts;
        box.setSuggestions(
          EFFORT_LEVELS.filter((e) => allowed.includes(e) && e.startsWith(partial)).map((e) => ({
            value: `/effort ${e}`,
            hint: EFFORT_HINT[e],
          })),
        );
      } else {
        // `includes` (not startsWith) so short aliases autocomplete too — "mini"
        // matches fx-mini, "pro" → fx-pro, "eco" → fx-eco.
        box.setSuggestions(
          TIER_IDS.filter(
            (id) => id.includes(partial) || TIERS[id].mode.toLowerCase().startsWith(partial),
          )
            .map((id) => ({ value: `/${cmd} ${id}`, hint: `${TIERS[id].mode} · ${TIER_HINT[id]}` }))
            .slice(0, 6),
        );
      }
      return;
    }
    box.setSuggestions([]);
  };

  const banner = (): void => {
    const stack = [ctx.card?.framework, ctx.card?.backend, ctx.card?.orm]
      .filter((x): x is string => Boolean(x))
      .join(' · ');
    const index = ctx.retriever
      ? `${ctx.retriever.size} chunks · ${ctx.retriever.symbols.stats().symbols} symbols`
      : 'none (run `futurex index`)';
    line();
    line(`  ${color.bold(color.magenta('◆ FutureX'))} ${color.dim('— agentic coding terminal')}`);
    line(`  ${color.dim('module')} ${color.cyan(tier)} ${color.dim(`· ${agentRuntime.toolCount} tools · index ${index}`)}`);
    if (stack) line(`  ${color.dim(`stack ${stack}`)}`);
    line(`  ${color.cyan('plan')} ${color.dim('→')} ${color.yellow('build')} ${color.dim('→')} ${color.green('verify')} ${color.dim('· writes ask first · checks run automatically after edits')}`);
    line(
      color.dim(
        box
          ? '  type below · ↑↓ pick suggestions · Shift+Tab auto-approve · queue while one runs · Ctrl+C cancels'
          : '  keep typing to queue · /help · /exit',
      ),
    );
    line();
  };

  // --- mount ----------------------------------------------------------------

  const useBox = process.stdin.isTTY === true && process.stdout.isTTY === true;
  if (useBox) {
    box = new Chatbox({
      status: statusLine,
      state: () => (running ? 'working' : 'ready'),
      handlers: {
        onSubmit: submit,
        onInterrupt: interrupt,
        onExit: () => finish(),
        onChange,
        onToggleAuto: toggleAuto,
      },
    });
  }

  // Per-project persistent gate: stored always-allow/deny decisions apply even
  // before any prompt; only an 'ask' state reaches the (TTY-only) modal.
  const ask: AskFn = box
    ? createChatboxAsk(box)
    : async () => {
        info('non-interactive: writes/exec denied — run futurex in a terminal to approve');
        return 'no';
      };
  const gate = createPermissionGate(ctx.root, ask, permMode);

  try {
    // Gateway-backed when logged in (the backend runs the loop); local direct
    // fallback otherwise — which needs FX_API_KEY.
    agentRuntime = createReplRuntime(ctx, opts, agentOptions, gate);
  } catch (err) {
    error(err instanceof Error ? err.message : String(err));
    error('run `futurex login` to use the FIM gateway, or set FX_API_KEY for local mode');
    process.exitCode = 1;
    return;
  }

  if (box) {
    setOutputSink(box);
    box.start();
    banner();
    process.on('SIGINT', interrupt);
    await new Promise<void>((resolve) => {
      finish = () => {
        closed = true;
        resolve();
      };
    });
    process.removeListener('SIGINT', interrupt);
    (active as AbortController | null)?.abort();
    box.stop();
    setOutputSink(null);
  } else {
    rl = createInterface({
      input: process.stdin,
      output: process.stdout,
      completer: (l: string): [string[], string] => {
        const vals = SLASH_COMMANDS.map((c) => c.value);
        if (!l.startsWith('/')) return [[], l];
        const hits = vals.filter((v) => v.startsWith(l.trimEnd()));
        return [hits.length > 0 ? hits : vals, l];
      },
    });
    banner();
    finish = () => {
      closed = true;
      rl?.close();
    };
    rl.on('SIGINT', interrupt);
    process.on('SIGINT', interrupt);
    rl.on('line', submit);
    rl.setPrompt(color.magenta('❯ '));
    rl.prompt();
    await new Promise<void>((resolve) => {
      rl?.once('close', () => {
        closed = true;
        resolve();
      });
    });
    (active as AbortController | null)?.abort();
    rl.removeListener('SIGINT', interrupt);
    process.removeListener('SIGINT', interrupt);
  }
}
